// Lightweight request cache utility
// Features:
// - Deduplicates in-flight requests: multiple callers for same key get same Promise
// - Caches successful responses for a TTL (default 10s)
// - Allows manual invalidation and clearing
// - Works with functions that return a Promise (e.g., axios requests)

const DEFAULT_TTL_MS = 10 * 1000; // 10 seconds

class RequestCache {
	constructor() {
		// Map<key, { expiresAt: number, value: any }>
		this.store = new Map();
		// Map<key, Promise> for in-flight dedupe
		this.inFlight = new Map();
	}

	_now() {
		return Date.now();
	}

	_isExpired(entry) {
		return !entry || (entry.expiresAt && entry.expiresAt <= this._now());
	}

	get(key) {
		const entry = this.store.get(key);
		if (!entry) return undefined;
		if (this._isExpired(entry)) {
			this.store.delete(key);
			return undefined;
		}
		return entry.value;
	}

	set(key, value, ttlMs = DEFAULT_TTL_MS) {
		const expiresAt = ttlMs ? this._now() + ttlMs : null;
		this.store.set(key, { value, expiresAt });
	}

	invalidate(key) {
		this.store.delete(key);
		this.inFlight.delete(key);
	}

	clear() {
		this.store.clear();
		this.inFlight.clear();
	}

	// main helper: returns cached value or runs factory to produce it
	// factory must return a Promise. If factory rejects, nothing is cached.
	async fetch(key, factory, ttlMs = DEFAULT_TTL_MS) {
		// 1) try cache
		const cached = this.get(key);
		if (cached !== undefined) {
			return cached;
		}

		// 2) dedupe in-flight
		let inflight = this.inFlight.get(key);
		if (inflight) return inflight;

		// 3) call factory and store in inFlight map
		const p = (async () => {
			try {
				const res = await factory();
				// only cache on success
				this.set(key, res, ttlMs);
				return res;
			} catch (err) {
				// do not cache errors; rethrow
				throw err;
			} finally {
				// remove in-flight after settled
				this.inFlight.delete(key);
			}
		})();

		this.inFlight.set(key, p);
		return p;
	}
}

const defaultRequestCache = new RequestCache();

module.exports = {
	RequestCache,
	defaultRequestCache,
};
